import React, { useEffect, useRef, useState } from 'react';
import { DillButton } from '../Dill/DillButton';
import { DillModal } from '../Dill/DillModal';
import { DillSelect } from '../Dill/DillSelect';
import { AppInputField } from '../AppInputField/AppInputField';
import { X } from 'react-feather';
import { Sparkle } from 'phosphor-react';
import AppFilePicker from '../AppFilePicker/AppFilePicker';
import { useAppDispatch, useAppSelector } from '../../../redux/store';
import { rewriteReminderTone } from '../../../redux/services/supplierBuyersService';
import { AiNotificationTone, Buyer, QuickbooksInvoice } from '@dill/dill-shared';
import { openDillToast } from '../../../utils/helpers';
import { APP_ENVIRONMENT } from '../../../utils/constants';
import moment from 'moment';

type ReminderType = 'PAYMENT' | 'CREDIT_HOLD';

interface BuyerUser {
  email: string;
  firstName?: string;
  lastName?: string;
  userId: string;
  isPaymentRemindersEmailOptOut: boolean;
}

interface AppCustomizePaymentReminderModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (data: {
    subjectLine: string;
    notes: string;
    attachments: File[];
    reminderType: ReminderType;
    excludedUserIds: string[];
    additionalEmails: string[];
    includeInvoicePdf: boolean;
    mergeInvoicePdf: boolean;
  }) => void;
  loading?: boolean;
  selectedInvoices?: QuickbooksInvoice[];
  defaultReminderType?: ReminderType;
  buyerUsers?: BuyerUser[];
  buyer?: Buyer | null;
}

const resolveDefaultIncludeInvoicePdf = (
  buyer: Buyer | null | undefined,
  supplier: { invoicePaymentReminderSettings?: any } | null | undefined
): boolean => {
  const buyerSetting = buyer?.invoicePaymentReminderSettings?.includeAttachments;
  if (buyerSetting !== null && buyerSetting !== undefined) {
    return buyerSetting;
  }
  const supplierSettings = supplier?.invoicePaymentReminderSettings;
  return !!(
    supplierSettings?.includeAttachments &&
    (!supplierSettings?.includeAttachmentsToSpecificBuyers ||
      (supplierSettings?.includeAttachmentsToSpecificBuyers &&
        buyer?.isPaymentRemindersIncludeAttachments))
  );
};

const resolveDefaultMergeInvoicePdf = (
  buyer: Buyer | null | undefined,
  supplier: { invoicePaymentReminderSettings?: any } | null | undefined
): boolean => {
  const buyerSetting = buyer?.invoicePaymentReminderSettings?.mergeAttachments;
  if (buyerSetting !== null && buyerSetting !== undefined) {
    return buyerSetting;
  }
  return !!supplier?.invoicePaymentReminderSettings?.mergeAttachments;
};

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB in bytes
const CREDIT_HOLD_NOTES =
  'To avoid any interruptions, please submit payment or respond with a payment update or remittance details.';

const getDaysPastDue = (dueDate: string | Date | null | undefined): number | null => {
  if (!dueDate) return null;
  const dueDateEndOfDay = moment(dueDate).endOf('day');
  return moment().diff(dueDateEndOfDay, 'days');
};

const getSubjectLine = (
  reminderType: ReminderType,
  supplierName: string,
  selectedInvoices: QuickbooksInvoice[]
): string => {
  const supplier = supplierName || '';

  if (selectedInvoices.length === 0) {
    return '';
  }

  if (selectedInvoices.length === 1) {
    const invoice = selectedInvoices[0];
    const docNumber = invoice.docNumber || '';
    const daysPastDue = getDaysPastDue(invoice.dueDate);

    if (daysPastDue !== null && daysPastDue > 0) {
      return reminderType === 'CREDIT_HOLD'
        ? `Approaching Credit Hold: Your ${supplier} invoice ${docNumber} is ${daysPastDue} days past due`
        : `Your ${supplier} invoice ${docNumber} is ${daysPastDue} days past due`;
    }

    return reminderType === 'CREDIT_HOLD'
      ? `Approaching Credit Hold: Your ${supplier} invoice ${docNumber} is past due`
      : `Your ${supplier} invoice ${docNumber} is past due`;
  }

  const count = selectedInvoices.length;
  return reminderType === 'CREDIT_HOLD'
    ? `Approaching Credit Hold: Your ${supplier} invoices (${count}) are past due`
    : `Your ${supplier} invoices (${count}) are past due`;
};

// Mirrors the real default body subtitle ("Details of your invoice X are as below") rendered
// by sendEmailService.ts when no customNotes is set - since this modal already has the real
// invoice data, this is the actual default, not an approximation.
const getDefaultNotes = (
  reminderType: ReminderType,
  supplierName: string,
  selectedInvoices: QuickbooksInvoice[]
): string => {
  if (reminderType === 'CREDIT_HOLD') {
    return CREDIT_HOLD_NOTES;
  }
  if (selectedInvoices.length === 0) {
    return '';
  }
  const supplier = supplierName || '';
  if (selectedInvoices.length === 1) {
    const docNumber = selectedInvoices[0].docNumber || '';
    return `Details of your ${supplier} invoice ${docNumber} are as below.`;
  }
  return `Details of your ${selectedInvoices.length} ${supplier} invoices are as below.`;
};

const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const TONE_OPTIONS: { label: string; value: AiNotificationTone }[] = [
  { label: '😐 Standard', value: 'STANDARD' },
  { label: '😊 Friendly', value: 'FRIENDLY' },
  { label: '😤 Firm', value: 'FIRM' },
  { label: '🧐 Formal', value: 'FORMAL' },
];

type AiGenerationState = 'default' | 'generating' | 'generated';

const AppCustomizePaymentReminderModal: React.FC<AppCustomizePaymentReminderModalProps> = ({
  open,
  handleClose,
  onSubmit,
  loading = false,
  selectedInvoices = [],
  defaultReminderType = 'PAYMENT',
  buyerUsers,
  buyer,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const prevOpenRef = useRef(false);
  const [subjectLine, setSubjectLine] = useState('');
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [fileSizeError, setFileSizeError] = useState<string>('');
  const [reminderType, setReminderType] = useState<ReminderType>(defaultReminderType);
  const [aiState, setAiState] = useState<AiGenerationState>('default');
  const [aiTone, setAiTone] = useState<AiNotificationTone>('STANDARD');
  const [defaultSubjectLine, setDefaultSubjectLine] = useState('');
  const [defaultNotes, setDefaultNotes] = useState('');
  const [excludedUserIds, setExcludedUserIds] = useState<string[]>([]);
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState('');
  const [includeInvoicePdf, setIncludeInvoicePdf] = useState(
    resolveDefaultIncludeInvoicePdf(buyer, currentSupplier)
  );
  const [mergeInvoicePdf, setMergeInvoicePdf] = useState(
    resolveDefaultMergeInvoicePdf(buyer, currentSupplier)
  );

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (open && !wasOpen) {
      setReminderType(defaultReminderType);
      const initialSubject = getSubjectLine(
        defaultReminderType,
        currentSupplier?.name || '',
        selectedInvoices
      );
      const initialNotes = getDefaultNotes(
        defaultReminderType,
        currentSupplier?.name || '',
        selectedInvoices
      );
      setSubjectLine(initialSubject);
      setNotes(initialNotes);
      setDefaultSubjectLine(initialSubject);
      setDefaultNotes(initialNotes);
      setAttachments([]);
      setFileSizeError('');
      setExcludedUserIds([]);
      setAdditionalEmails([]);
      setNewEmailInput('');
      setEmailInputError('');
      setIncludeInvoicePdf(resolveDefaultIncludeInvoicePdf(buyer, currentSupplier));
      setMergeInvoicePdf(resolveDefaultMergeInvoicePdf(buyer, currentSupplier));
      setAiState('default');
      setAiTone('STANDARD');
    }
  }, [open, selectedInvoices, defaultReminderType, buyer, currentSupplier]);

  useEffect(() => {
    if (open) {
      const nextSubject = getSubjectLine(
        reminderType,
        currentSupplier?.name || '',
        selectedInvoices
      );
      const nextNotes = getDefaultNotes(
        reminderType,
        currentSupplier?.name || '',
        selectedInvoices
      );
      setSubjectLine(nextSubject);
      setNotes(nextNotes);
      setDefaultSubjectLine(nextSubject);
      setDefaultNotes(nextNotes);
      setAiState('default');
    }
  }, [reminderType, open, selectedInvoices]);

  const defaultRecipients = buyerUsers ?? [];
  const activeDefaultRecipients = defaultRecipients.filter(
    (u) => !excludedUserIds.includes(u.userId)
  );
  const totalRecipients = activeDefaultRecipients.length + additionalEmails.length;
  const hasRecipientControl = buyerUsers !== undefined;

  const handleAddEmail = () => {
    const trimmed = newEmailInput.trim();
    if (!isValidEmail(trimmed)) {
      setEmailInputError('Please enter a valid email address.');
      return;
    }
    const alreadyDefault = defaultRecipients.some(
      (u) => u.email.toLowerCase() === trimmed.toLowerCase()
    );
    const alreadyAdded = additionalEmails.some((e) => e.toLowerCase() === trimmed.toLowerCase());
    if (alreadyDefault || alreadyAdded) {
      setEmailInputError('This email is already in the recipient list.');
      return;
    }
    setAdditionalEmails((prev) => [...prev, trimmed]);
    setNewEmailInput('');
    setEmailInputError('');
  };

  const handleSubmit = () => {
    const trimmed = newEmailInput.trim();

    if (trimmed) {
      if (!isValidEmail(trimmed)) {
        setEmailInputError('Please enter a valid email address.');
        return;
      }
      const alreadyExists =
        activeDefaultRecipients.some((u) => u.email.toLowerCase() === trimmed.toLowerCase()) ||
        additionalEmails.some((e) => e.toLowerCase() === trimmed.toLowerCase());
      if (alreadyExists) {
        setEmailInputError('This email is already in the recipient list.');
        return;
      }
      setEmailInputError('');
      setNewEmailInput('');
    }

    const finalEmails = trimmed ? [...additionalEmails, trimmed] : additionalEmails;

    if (trimmed) {
      setAdditionalEmails(finalEmails);
    }

    onSubmit({
      subjectLine,
      notes,
      attachments,
      reminderType,
      excludedUserIds,
      additionalEmails: finalEmails,
      includeInvoicePdf,
      mergeInvoicePdf,
    });
  };

  const handleCloseModal = () => {
    setSubjectLine('');
    setNotes('');
    setAttachments([]);
    setFileSizeError('');
    setExcludedUserIds([]);
    setAdditionalEmails([]);
    setNewEmailInput('');
    setEmailInputError('');
    setIncludeInvoicePdf(resolveDefaultIncludeInvoicePdf(buyer, currentSupplier));
    setMergeInvoicePdf(resolveDefaultMergeInvoicePdf(buyer, currentSupplier));
    setAiState('default');
    setAiTone('STANDARD');
    handleClose();
  };

  const generateWithTone = async (tone: AiNotificationTone) => {
    if (!currentSupplier?.id || !buyer?.id) {
      openDillToast({ message: 'Select a customer to generate AI content', type: 'ERROR' });
      return;
    }

    const priorState = aiState;
    setAiState('generating');
    try {
      const result = await dispatch(
        rewriteReminderTone({
          supplierId: currentSupplier.id,
          buyerId: buyer.id,
          reminderType: reminderType === 'CREDIT_HOLD' ? 'CREDIT_HOLD' : 'DUE_DATE',
          tone,
          currentSubject: defaultSubjectLine,
          currentNotes: defaultNotes,
        })
      );

      const payload = result.payload as { subject?: string; notes?: string } | null;
      if (result.type !== 'supplierBuyers/rewriteReminderTone/fulfilled' || !payload?.subject) {
        openDillToast({ message: 'AI generation failed. Please try again.', type: 'ERROR' });
        setAiState(priorState);
        return;
      }

      setSubjectLine(payload.subject);
      setNotes(payload.notes || '');
      setAiTone(tone);
      setAiState('generated');
    } catch (error) {
      console.error('Failed to generate AI content:', error);
      openDillToast({ message: 'AI generation failed. Please try again.', type: 'ERROR' });
      setAiState(priorState);
    }
  };

  const handleSwitchToDefaultMessage = () => {
    setSubjectLine(defaultSubjectLine);
    setNotes(defaultNotes);
    setAiState('default');
  };

  const handleFileSelect = (files: File[]) => {
    const oversizedFiles = files.filter((file) => file.size > MAX_FILE_SIZE);

    if (oversizedFiles.length > 0) {
      const fileNames = oversizedFiles.map((f) => f.name).join(', ');
      setFileSizeError(
        `The following file(s) exceed the 3MB limit: ${fileNames}. Please select smaller files.`
      );
      setAttachments([]);
    } else {
      setFileSizeError('');
      setAttachments(files);
    }
  };

  const pendingEmailIsValid = !!newEmailInput.trim() && isValidEmail(newEmailInput.trim());
  const isSendDisabled =
    !subjectLine ||
    loading ||
    !!fileSizeError ||
    (hasRecipientControl && totalRecipients === 0 && !pendingEmailIsValid);

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Customize Payment Reminder"
      maxWidth="min(90vw, 720px)"
      footer={
        <>
          <DillButton label="Cancel" variant="secondary" onClick={handleCloseModal} />
          <DillButton
            label="Send Reminder"
            onClick={handleSubmit}
            isDisabled={isSendDisabled}
            isLoading={loading}
          />
        </>
      }>
      <div className="space-y-5">
        {/* Reminder type — pill segmented control */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Reminder Type
          </p>
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="reminderType"
                value="PAYMENT"
                checked={reminderType === 'PAYMENT'}
                onChange={(e) => setReminderType(e.target.value as ReminderType)}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">Payment Reminder</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="reminderType"
                value="CREDIT_HOLD"
                checked={reminderType === 'CREDIT_HOLD'}
                onChange={(e) => setReminderType(e.target.value as ReminderType)}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm">Credit Hold Reminder</span>
            </label>
          </div>
        </div>

        {/* Recipients */}
        {hasRecipientControl && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-3">
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Recipients
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                Automatically populated emails from your customer's users, but you can exclude any
                emails or add additional ones before sending.
              </p>
            </div>

            {(activeDefaultRecipients.length > 0 || additionalEmails.length > 0) && (
              <div className="flex flex-wrap gap-1.5">
                {activeDefaultRecipients.map((u) => (
                  <span
                    key={u.userId}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-white border border-gray-200 rounded-full text-xs text-gray-700 shadow-sm">
                    {u.email}
                    <button
                      type="button"
                      onClick={() => setExcludedUserIds((prev) => [...prev, u.userId])}
                      className="ml-0.5 text-gray-400 hover:text-gray-700 cursor-pointer bg-transparent border-none leading-none">
                      <X size={11} />
                    </button>
                  </span>
                ))}
                {additionalEmails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 border border-blue-200 rounded-full text-xs text-blue-700">
                    {email}
                    <button
                      type="button"
                      onClick={() => setAdditionalEmails((prev) => prev.filter((e) => e !== email))}
                      className="ml-0.5 text-blue-400 hover:text-blue-700 cursor-pointer bg-transparent border-none leading-none">
                      <X size={11} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {totalRecipients === 0 && !pendingEmailIsValid && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                No recipients selected — add at least one email to send.
              </p>
            )}

            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <AppInputField
                  placeholder="Add email address"
                  value={newEmailInput}
                  onTextChange={(text) => {
                    setNewEmailInput(text);
                    if (emailInputError) setEmailInputError('');
                  }}
                />
                {emailInputError && <p className="text-xs text-red-600 mt-1">{emailInputError}</p>}
              </div>
              <DillButton
                label="Add"
                variant="secondary"
                onClick={handleAddEmail}
                className="mt-0.5 whitespace-nowrap"
              />
            </div>
          </div>
        )}

        {/* AI-generated wording — not ready for production yet */}
        {APP_ENVIRONMENT !== 'production' && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3.5 py-2.5">
            {aiState === 'default' && (
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                  Default Message
                </span>
                <button
                  type="button"
                  onClick={() => generateWithTone(aiTone)}
                  disabled={!buyer?.id}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed bg-transparent border-none cursor-pointer">
                  <Sparkle size={13} weight="bold" />
                  Rewrite message
                </button>
              </div>
            )}

            {aiState === 'generating' && (
              <div className="flex items-center gap-2">
                <Sparkle size={13} weight="bold" className="text-purple-500 animate-pulse" />
                <span className="text-xs font-medium text-purple-700">
                  Generating message based on customer history and invoice status…
                </span>
              </div>
            )}

            {aiState === 'generated' && (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <Sparkle size={13} weight="bold" className="text-purple-600 shrink-0" />
                  <span className="text-xs font-semibold text-purple-700 shrink-0">Generated</span>
                  <div className="w-32">
                    <DillSelect
                      value={aiTone}
                      onChange={(v) => generateWithTone(v as AiNotificationTone)}
                      options={TONE_OPTIONS}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => generateWithTone(aiTone)}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700 bg-transparent border-none cursor-pointer">
                    Rewrite
                  </button>
                  <DillButton
                    label="Switch to default message"
                    variant="secondary"
                    onClick={handleSwitchToDefaultMessage}
                    height={30}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Subject line */}
        <AppInputField
          label="Subject Line"
          placeholder={aiState === 'generating' ? 'Writing subject…' : 'Enter email subject'}
          value={aiState === 'generating' ? '' : subjectLine}
          onTextChange={(text) => setSubjectLine(text)}
          isDisabled={aiState === 'generating'}
          isRequired
        />

        {/* Notes */}
        <AppInputField
          label={reminderType === 'CREDIT_HOLD' ? 'Credit Hold Message' : 'Message'}
          placeholder={
            aiState === 'generating'
              ? 'Writing message based on customer history and invoice status…'
              : reminderType === 'CREDIT_HOLD'
              ? 'Add message to include in the credit hold section of email'
              : 'Add any additional notes to include in the email body'
          }
          inputType="textarea"
          value={aiState === 'generating' ? '' : notes}
          onTextChange={(text) => setNotes(text)}
          isDisabled={aiState === 'generating'}
        />

        {APP_ENVIRONMENT !== 'production' && aiState === 'generated' && (
          <p className="text-[11px] text-gray-400 -mt-3">
            Review before you send. Important information about how we use generative AI.
          </p>
        )}

        {/* Credit hold email preview */}
        {reminderType === 'CREDIT_HOLD' && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
              Email Preview
            </p>
            <div className="bg-red-50 border-2 border-red-400 rounded-xl p-5 mx-auto w-[90%] max-w-[460px]">
              <p className="font-semibold text-sm text-red-600 text-center mb-2">
                Your account is approaching credit hold status
              </p>
              <p className="text-sm text-gray-700 text-center leading-relaxed">{notes}</p>
              <p className="text-sm font-medium text-gray-700 text-center mt-3">
                Thank you,
                <br />
                Accounts Receivable Team
              </p>
            </div>
          </div>
        )}

        {/* Invoice PDF attachment options */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Invoice PDF
          </p>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeInvoicePdf}
              onChange={(e) => setIncludeInvoicePdf(e.target.checked)}
              className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
            />
            <span className="text-sm">
              Attach invoice PDF{selectedInvoices.length > 1 ? 's' : ''}
            </span>
          </label>
          {includeInvoicePdf && selectedInvoices.length > 1 && (
            <div className="flex gap-6 mt-2.5 ml-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mergeInvoicePdf"
                  checked={mergeInvoicePdf}
                  onChange={() => setMergeInvoicePdf(true)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Combine into one file</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="mergeInvoicePdf"
                  checked={!mergeInvoicePdf}
                  onChange={() => setMergeInvoicePdf(false)}
                  className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm">Separate file per invoice</span>
              </label>
            </div>
          )}
        </div>

        {/* Attachments */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Attachments <span className="normal-case font-normal text-gray-400">(optional)</span>
          </p>
          <AppFilePicker
            onSelectFile={handleFileSelect}
            onDropProps={{
              accept: {
                'application/pdf': ['.pdf'],
                'image/*': ['.png', '.jpg', '.jpeg'],
                'application/msword': ['.doc'],
                'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
                  '.docx',
                ],
              },
            }}
          />
          {fileSizeError && (
            <p className="text-xs text-red-600 mt-2" role="alert">
              {fileSizeError}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-1.5">Maximum file size: 3MB per file</p>
        </div>
      </div>
    </DillModal>
  );
};

export default AppCustomizePaymentReminderModal;
